import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { format } from "date-fns";

interface PDFExportOptions {
  dashboardElement: HTMLElement;
  fileName?: string;
  userName?: string;
  companyName?: string;
  companyLogo?: string;
}

const drawHeader = async (
  pdf: jsPDF,
  companyName: string,
  companyLogo?: string
) => {
  const margin = 15;
  const pageWidth = 210;
  
  // Draw company logo if available
  if (companyLogo) {
    try {
      pdf.addImage(companyLogo, "PNG", margin, margin, 15, 15);
      pdf.setFontSize(14);
      pdf.setFont("helvetica", "bold");
      pdf.text(companyName, margin + 20, margin + 10);
    } catch (error) {
      // If logo fails, just show company name
      pdf.setFontSize(14);
      pdf.setFont("helvetica", "bold");
      pdf.text(companyName, margin, margin + 10);
    }
  } else {
    pdf.setFontSize(14);
    pdf.setFont("helvetica", "bold");
    pdf.text(companyName, margin, margin + 10);
  }
  
  // Draw horizontal line
  pdf.setDrawColor(226, 232, 240); // #e2e8f0
  pdf.setLineWidth(0.5);
  pdf.line(margin, margin + 18, pageWidth - margin, margin + 18);
};

const drawFooter = (
  pdf: jsPDF,
  userName: string,
  pageNumber: number
) => {
  const margin = 15;
  const pageWidth = 210;
  const pageHeight = 297;
  const footerY = pageHeight - margin - 10;
  
  // Draw horizontal line
  pdf.setDrawColor(226, 232, 240); // #e2e8f0
  pdf.setLineWidth(0.5);
  pdf.line(margin, footerY - 3, pageWidth - margin, footerY - 3);
  
  // Draw footer text
  pdf.setFontSize(9);
  pdf.setFont("helvetica", "normal");
  pdf.setTextColor(100, 100, 100);
  
  const now = new Date();
  const dateStr = format(now, "dd/MM/yyyy");
  const timeStr = format(now, "HH:mm");
  const footerText = `Dashboard criada em ${dateStr} às ${timeStr} por ${userName} no Dashi.app.br`;
  
  pdf.text(footerText, margin, footerY + 3);
};

export const exportDashboardToPDF = async ({
  dashboardElement,
  fileName = "dashboard-report.pdf",
  userName = "Usuário",
  companyName = "Dashi",
  companyLogo,
}: PDFExportOptions) => {
  try {
    // Capture the dashboard as canvas
    const canvas = await html2canvas(dashboardElement, {
      scale: 2,
      logging: false,
      useCORS: true,
      allowTaint: true,
    });

    const imgData = canvas.toDataURL("image/png");
    
    // PDF dimensions with margins
    const margin = 15; // 1.5cm margin
    const pageWidth = 210; // A4 width in mm
    const pageHeight = 297; // A4 height in mm
    const headerHeight = 20;
    const footerHeight = 15;
    const contentWidth = pageWidth - (margin * 2);
    const contentHeight = pageHeight - (margin * 2) - headerHeight - footerHeight;
    
    // Calculate content dimensions
    const imgWidth = contentWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;
    let position = margin + headerHeight;

    // Create PDF
    const pdf = new jsPDF("p", "mm", "a4");
    
    // Draw header and footer on first page
    await drawHeader(pdf, companyName, companyLogo);
    drawFooter(pdf, userName, 1);
    
    // Add content to first page
    pdf.addImage(
      imgData, 
      "PNG", 
      margin, 
      position, 
      imgWidth, 
      imgHeight
    );
    heightLeft -= contentHeight;

    let pageNumber = 1;
    
    // Add additional pages if needed
    while (heightLeft > 0) {
      pageNumber++;
      position = -(imgHeight - heightLeft) + margin + headerHeight;
      pdf.addPage();
      
      // Draw header and footer on each page
      await drawHeader(pdf, companyName, companyLogo);
      drawFooter(pdf, userName, pageNumber);
      
      pdf.addImage(
        imgData, 
        "PNG", 
        margin, 
        position, 
        imgWidth, 
        imgHeight
      );
      heightLeft -= contentHeight;
    }

    // Save PDF
    pdf.save(fileName);
  } catch (error) {
    console.error("Error generating PDF:", error);
    throw new Error("Falha ao gerar PDF");
  }
};
